// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { FundRecord, validateFundRecord } from '../../../state_manager/data_models/fund_record.ts'
import {
  validateFundMobileListRecord,
  type FundMobileListRecord
} from '../../../state_manager/data_models/fund_mobile_list_record.ts'
import { FilterValue, SortConfig, FundsPaginatedResponse } from '../../../state_manager/interfaces/funds.ts'
import { FundTermsRecord, validateFundTermsRecord } from '../../../state_manager/data_models/fund_terms_record.ts'
import {
  FinYearAllocationOverride,
  FundFinYearAllocation,
  validateFundFinYearAllocation
} from '../../../state_manager/data_models/fund_fin_year_allocation.ts'
import {
  FundPartnerAllocation,
  validateFundPartnerAllocation
} from '../../../state_manager/data_models/fund_partner_allocation.ts'
import { filenameFromContentDisposition } from '../../../core/utils/contentDisposition.ts'
import { saveBlob } from '../../document_reports/services/documentReportsService.ts'
import {
  validateFundWaterfallRecord,
  FundWaterfallRecord,
  FundWaterfallSavePayload
} from '../../../state_manager/data_models/fund_waterfall_record.ts'
import {
  FundWaterfallPosition,
  validateFundWaterfallPosition
} from '../../../state_manager/data_models/fund_waterfall_position_record.ts'
import { FundNoticeSettings, validateFundNoticeSettings } from '../../../state_manager/data_models/fund_notice_settings.ts'
import { FundStatementSettings, validateFundStatementSettings } from '../../../state_manager/data_models/fund_statement_settings.ts'
import {
  FundTrialBalanceRow,
  FundLedgerFinYear,
  FundLedgerTotals,
  FundGlDetailRow,
  validateFundTrialBalanceRows,
  validateFundGlDetailRows
} from '../../../state_manager/data_models/fund_ledger.ts'
import {
  FundStatementRow,
  FundStatementMeta,
  FundStatementColumn,
  FundStatementLayout,
  FundPartnersCapitalRow,
  FundPartnersCapitalTotal,
  FundLpCapitalAccountHeader,
  FundStatementTypeOption,
  FundStatementLpOption,
  validateFundStatementRows,
  validateFundStatementColumns,
  validateFundPartnersCapitalRows,
  validateFundStatementTypeOptions,
  validateFundStatementLpOptions
} from '../../../state_manager/data_models/fund_statements.ts'
import {
  validateInvestorStatementChecks,
  type InvestorStatementChecks
} from '../../../state_manager/data_models/investor_statement_checks.ts'
import {
  FundChartAccountRow,
  FundChartItemRow,
  ChartGlAccountOption,
  ChartSourceFundOption,
  ChartTemplateOption,
  validateFundChartAccountRows,
  validateFundChartItemRows
} from '../../../state_manager/data_models/fund_chart_of_accounts.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'
import {
  type FundCommitmentEditorOptions,
  validateFundCommitmentEditorOptions,
  validateFundCommitmentSaveResult
} from '../../../state_manager/data_models/fund_commitment_editor.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export type { FundsPaginatedResponse }

export interface FundsTablegridResponse {
  data: Record<string, unknown>[]
  error?: string
}

export interface FundsMobileListResponse {
  data: FundMobileListRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

/**
 * Lean mobile funds list — hits `/funds/mobile-list` (the view__funds_mobile_list RPC),
 * which returns only id + name + closed capital + FMV + MOIC, sorted by name + paginated.
 * ~5-6x faster and a fraction of the payload of `fetchFundsFiltered` (the grid RPC pulls
 * cashflows + ~79 columns to compute a Gross IRR the mobile row doesn't show).
 */
export const fetchFundsMobileList = async (
  page: number = 1,
  pageSize: number = 25,
  accessToken: string | null = null
): Promise<FundsMobileListResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = { data: { page, page_size: pageSize } }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.MOBILE_LIST), {
      method: API_ROUTES.FUNDS.MOBILE_LIST.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch mobile funds list: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateFundMobileListRecord(parsed.data || [], { isArray: true }) as FundMobileListRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching mobile funds list:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching funds'
    }
  }
}

export interface FundDashboardDetailResponse {
  data: Record<string, unknown> | null
  error?: string
}

export interface FundInvestmentSummaryResponse {
  data: Record<string, unknown>[]
  error?: string
}

export const fetchFundsTablegrid = async (
  accessToken: string | null = null,
  options?: {
    fundIds?: string[] | null
    datetime?: string | null
    onlyManagedByUs?: boolean
    includeWarehoused?: boolean
  }
): Promise<FundsTablegridResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams: Record<string, string> = {}
    if (options?.fundIds && options.fundIds.length > 0) {
      queryParams.fund_ids = options.fundIds.join(',')
    }
    if (options?.datetime) {
      queryParams.datetime = options.datetime
    }
    if (options?.onlyManagedByUs === false) {
      queryParams.only_managed_by_us = 'false'
    }
    if (options?.includeWarehoused === true) {
      queryParams.include_warehoused = 'true'
    }

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.LIST, {}, queryParams)

    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.LIST.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch funds: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: Record<string, unknown>[] }

    return {
      data: Array.isArray(parsed.data) ? parsed.data : []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching funds tablegrid:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: {
    reportId?: string | null
    datetime?: string | null
    onlyManagedByUs?: boolean
    includeWarehoused?: boolean
  }
): Promise<FundsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const effectivePageSize = pageSize ?? 200

    const requestBody: {
      data: {
        sorting?: Array<{ key: string; direction: string }>
        filters?: Array<{ key: string; value: any }>
        page?: number
        page_size?: number
        report_id?: string | null
        datetime?: string | null
        only_managed_by_us?: boolean
        include_warehoused?: boolean
      }
    } = {
      data: { page, page_size: effectivePageSize }
    }

    requestBody.data.sorting = sorting.map((s) => ({ key: s.key, direction: s.direction }))
    requestBody.data.filters = filters.map((f) => ({ key: f.key, value: f.value }))

    if (options?.reportId) requestBody.data.report_id = options.reportId
    if (options?.datetime) requestBody.data.datetime = options.datetime
    if (options?.onlyManagedByUs === false) requestBody.data.only_managed_by_us = false
    if (options?.includeWarehoused === true) requestBody.data.include_warehoused = true

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FILTER), {
      method: API_ROUTES.FUNDS.FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch filtered funds: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    // Validate each row, but be tolerant: drop rows that fail validation rather
    // than failing the whole request.
    const validated: FundRecord[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateFundRecord(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Funds row failed validation:', e, row)
        }
      }
    }

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching filtered funds:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching funds'
    }
  }
}

export interface FundsFilterOptionsResponse {
  data: Record<string, Array<{ value: string; label: string }>>
  error?: string
}

/**
 * Returns filter-option lists for the Funds tablegrid (currently `funds__id`
 * for the Name multiselect). Fetched once on mount; the option set only
 * changes if the user toggles managed_by_us / include_warehoused, which
 * isn't currently surfaced on the Funds tablegrid.
 */
export const fetchFundsFilterOptions = async (
  accessToken: string | null = null
): Promise<FundsFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FILTER_OPTIONS, {}, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.FILTER_OPTIONS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch funds filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data || {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching funds filter options:', error)
    }
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Typeahead search for funds. Reuses /funds/filter with a text-contains filter
 * on funds__name (no separate /funds/search endpoint — funds is a small dataset
 * so we just hit the filter endpoint with a small page).
 */
export const searchFundsForPopup = async (
  searchTerm: string,
  maxResults: number = 20,
  accessToken: string | null = null
): Promise<{ data: Record<string, unknown>[]; error?: string }> => {
  const term = (searchTerm || '').trim()
  if (term.length < 1) return { data: [] }

  try {
    const response = await fetchFundsFiltered(
      [{ key: 'funds__name', direction: 'asc' }],
      [{ key: 'funds__name', value: term }],
      1,
      Math.max(1, maxResults),
      accessToken
    )
    if (response.error) return { data: [], error: response.error }
    // Drop the grand-total row from search results.
    const filtered = response.data.filter((r: any) => r && r.funds__id) as Record<string, unknown>[]
    return { data: filtered }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error searching funds:', error)
    return { data: [], error: error instanceof Error ? error.message : 'Search failed' }
  }
}

/**
 * POST /funds — create a fund from the New Fund form (app admins only; the
 * backend returns 403 otherwise). `payload` carries bare column names
 * (name, legal_name, functional_currency_id, ...). Resolves with the new
 * record's dashboard detail — the same shape GET /funds/:id returns — so the
 * caller can seed activeRecords / the funds slice before the tab morphs.
 */
export const createFund = async (
  payload: Record<string, unknown>,
  accessToken: string | null = null
): Promise<FundDashboardDetailResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.CREATE, {}, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.CREATE.method,
      headers,
      body: JSON.stringify(payload)
    })

    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, unknown> | null
      message?: string
      error?: string
    }
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.error || json.message || `Failed to create fund: ${response.status}`)
    }
    return { data: json.data ?? null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error creating fund:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundDashboardDetail = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundDashboardDetailResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.GET, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.GET.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, unknown> | null }
    return { data: json.data ?? null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface FundInvestmentSummaryPaginatedResponse {
  data: Record<string, unknown>[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface FundInvestmentSummaryFilterOptionsResponse {
  data: Record<string, Array<{ value: string; label: string }>>
  error?: string
}

export const fetchFundInvestmentSummaryFiltered = async (
  fundId: string,
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: { datetime?: string | null; onlyManagedByUs?: boolean; includeWarehoused?: boolean }
): Promise<FundInvestmentSummaryPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody: { data: Record<string, unknown> } = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }
    if (options?.datetime) (requestBody.data as any).datetime = options.datetime
    if (options?.onlyManagedByUs === false) (requestBody.data as any).only_managed_by_us = false
    if (options?.includeWarehoused === true) (requestBody.data as any).include_warehoused = true

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTMENTS_FILTER, { id: fundId }, {}),
      {
        method: API_ROUTES.FUNDS.INVESTMENTS_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund investment summary: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? (json.data as Record<string, unknown>[]) : []
    return {
      data: rows,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching fund investment summary:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundInvestmentSummaryFilterOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundInvestmentSummaryFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTMENTS_FILTER_OPTIONS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.INVESTMENTS_FILTER_OPTIONS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(
        `Failed to fetch fund investment summary filter options: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data || {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching fund investment summary filter options:', error)
    }
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Typeahead search over the companies invested in by a fund. Reuses the
 * filter endpoint with a name-contains text filter. Used by the inline
 * SearchPopover on the Investment Summary toolbar.
 */
export const searchCompaniesInFundForPopup = async (
  fundId: string,
  searchTerm: string,
  maxResults: number = 20,
  accessToken: string | null = null
): Promise<{ data: Record<string, unknown>[]; error?: string }> => {
  const term = (searchTerm || '').trim()
  if (term.length < 1) return { data: [] }

  const response = await fetchFundInvestmentSummaryFiltered(
    fundId,
    [{ key: 'accounts__name', direction: 'asc' }],
    [{ key: 'accounts__name', value: term }],
    1,
    Math.max(1, maxResults),
    accessToken
  )
  if (response.error) return { data: [], error: response.error }
  // Drop the grand-total row from search results.
  const filtered = response.data.filter((r: any) => r && r.accounts__id) as Record<string, unknown>[]
  return { data: filtered }
}

export const fetchFundInvestmentSummary = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundInvestmentSummaryResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTMENTS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.INVESTMENTS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund investments: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, unknown>[] }
    return { data: Array.isArray(json.data) ? json.data : [] }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund investments:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ============================================================================
// Fund Investment Detail (configurable per-fund grid with user-selected aggregations)
// ============================================================================

export interface FundInvestmentDetailPaginatedResponse {
  data: Record<string, unknown>[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface FundInvestmentDetailFilterOptionsResponse {
  data: Record<string, Array<{ value: string; label: string }>>
  error?: string
}

export const fetchFundInvestmentDetailFiltered = async (
  fundId: string,
  aggregation: string[] = [],
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: {
    datetime?: string | null
    onlyManagedByUs?: boolean
    includeWarehoused?: boolean
    includeConvertedOut?: boolean
  }
): Promise<FundInvestmentDetailPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody: { data: Record<string, unknown> } = {
      data: {
        page,
        page_size: pageSize ?? 200,
        aggregation,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }
    if (options?.datetime) (requestBody.data as any).datetime = options.datetime
    if (options?.onlyManagedByUs === false) (requestBody.data as any).only_managed_by_us = false
    if (options?.includeWarehoused === true) (requestBody.data as any).include_warehoused = true
    if (options?.includeConvertedOut === true) (requestBody.data as any).include_converted_out = true

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTMENT_DETAIL_FILTER, { id: fundId }, {}),
      {
        method: API_ROUTES.FUNDS.INVESTMENT_DETAIL_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund investment detail: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? (json.data as Record<string, unknown>[]) : []
    return {
      data: rows,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching fund investment detail:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundInvestmentDetailFilterOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundInvestmentDetailFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTMENT_DETAIL_FILTER_OPTIONS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.INVESTMENT_DETAIL_FILTER_OPTIONS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(
        `Failed to fetch fund investment detail filter options: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data || {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching fund investment detail filter options:', error)
    }
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ============================================================================
// Standalone Investments screen — multi-fund / all-funds Investment Detail.
// Mirrors fetchFundInvestmentDetail* but scope is a fund-id array (empty =
// all funds) passed in the request body rather than a path param.
// ============================================================================

export const fetchInvestmentDetailFiltered = async (
  fundIds: string[] = [],
  aggregation: string[] = [],
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: {
    datetime?: string | null
    onlyManagedByUs?: boolean
    includeWarehoused?: boolean
    includeConvertedOut?: boolean
    includeIrr?: boolean
  }
): Promise<FundInvestmentDetailPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody: { data: Record<string, unknown> } = {
      data: {
        fund_ids: fundIds,
        page,
        page_size: pageSize ?? 200,
        aggregation,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }
    if (options?.datetime) (requestBody.data as any).datetime = options.datetime
    if (options?.onlyManagedByUs === false) (requestBody.data as any).only_managed_by_us = false
    if (options?.includeWarehoused === true) (requestBody.data as any).include_warehoused = true
    if (options?.includeConvertedOut === true) (requestBody.data as any).include_converted_out = true
    if (options?.includeIrr === true) (requestBody.data as any).include_irr = true

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.STANDALONE_INVESTMENT_DETAIL_FILTER, {}, {}),
      {
        method: API_ROUTES.FUNDS.STANDALONE_INVESTMENT_DETAIL_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch investment detail: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? (json.data as Record<string, unknown>[]) : []
    return {
      data: rows,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching investment detail:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchInvestmentDetailFilterOptions = async (
  fundIds: string[] = [],
  accessToken: string | null = null,
  options?: { datetime?: string | null }
): Promise<FundInvestmentDetailFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody: { data: Record<string, unknown> } = { data: { fund_ids: fundIds } }
    if (options?.datetime) (requestBody.data as any).datetime = options.datetime

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.STANDALONE_INVESTMENT_DETAIL_FILTER_OPTIONS, {}, {}),
      {
        method: API_ROUTES.FUNDS.STANDALONE_INVESTMENT_DETAIL_FILTER_OPTIONS.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch investment detail filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data || {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching investment detail filter options:', error)
    }
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ============================================================================
// Fund Investors tab (per-fund LP commitments grid + summary)
// ============================================================================

export interface FundInvestorsSummaryResponse {
  data: Record<string, unknown> | null
  error?: string
}

export interface FundInvestorsPaginatedResponse {
  data: Record<string, unknown>[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface FundInvestorsFilterOptionsResponse {
  data: Record<string, Array<{ value: string; label: string }>>
  error?: string
}

export const fetchFundInvestorsSummary = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundInvestorsSummaryResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTORS_SUMMARY, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.INVESTORS_SUMMARY.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund investors summary: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, unknown> | null }
    return { data: json.data ?? null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund investors summary:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundInvestorsFiltered = async (
  fundId: string,
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<FundInvestorsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTORS_FILTER, { id: fundId }, {}),
      {
        method: API_ROUTES.FUNDS.INVESTORS_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund investors: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? (json.data as Record<string, unknown>[]) : []
    return {
      data: rows,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund investors:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundInvestorsFilterOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundInvestorsFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.INVESTORS_FILTER_OPTIONS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.INVESTORS_FILTER_OPTIONS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund investors filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data || {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund investors filter options:', error)
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ============================================================================
// Fund Terms (Terms tab)
// ============================================================================

export interface FundTermsResponse {
  data: FundTermsRecord | null
  error?: string
}

export interface FundFeePeriodPayload {
  id?: string
  term: number | null
  term_units_id: number | null
  base_fee_rate: number | null
  basis_id: number | null
  reduction_scheme_id: number | null
  reduction_rate?: number | null
  reduction_frequency_quarters?: number | null
  fee_floor_rate?: number | null
}

/**
 * Full-replacement save of a fund's fee-period schedule. Rows omitted from
 * the list are soft-trashed; sequence follows the array order. Returns the
 * fresh period list.
 */
export const saveFundFeePeriods = async (
  fundId: string,
  periods: FundFeePeriodPayload[],
  accessToken: string | null = null
): Promise<{ data: Record<string, unknown>[]; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FEE_PERIODS_SAVE, { id: fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.FEE_PERIODS_SAVE.method,
      headers,
      body: JSON.stringify({ data: { periods } })
    })

    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, unknown>[]
      message?: string
      error?: string
    }
    if (!response.ok) {
      throw new Error(json.error || json.message || `Failed to save fee periods: ${response.status}`)
    }
    return { data: json.data ?? [] }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving fee periods:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * The fund's capital call notice settings (Terms tab notice sections): layout,
 * wording overrides and bank account, with the active layouts. Writes go
 * through InputManager's auto-patch (API_ROUTES.FUNDS.NOTICE_SETTINGS_PATCH /
 * NOTICE_BANK_ACCOUNT_PATCH), one field at a time.
 */
export const fetchFundNoticeSettings = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: FundNoticeSettings | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.NOTICE_SETTINGS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.NOTICE_SETTINGS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch notice settings: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateFundNoticeSettings(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund notice settings:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Investor statement settings of a fund (view__fund_statement_settings): the layout, the
 * partners' capital page and the wording overrides, plus the layouts and
 * the default wording. The fields are written through InputManager's
 * auto-patch (API_ROUTES.FUNDS.STATEMENT_SETTINGS_PATCH), one at a time.
 */
export const fetchFundStatementSettings = async (
  id: string,
  accessToken: string | null = null
): Promise<{ data: FundStatementSettings | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.STATEMENT_SETTINGS, { id }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.STATEMENT_SETTINGS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch statement settings: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateFundStatementSettings(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement settings:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** The Commitment form's save of a standalone commitment. Create: fund_id +
 *  investor_id OR new_investor. Edit: id + only the changed columns (null
 *  clears). Rates are fractions (0.02 = 2%); dates 'YYYY-MM-DD'. A pocket of
 *  a consolidated commitment takes id + is_gp_commitment only (its notice
 *  documents are set on the consolidated commitment). The GP switch of the
 *  Investors grid sends id + is_gp_commitment alone, on standalone and pocket
 *  rows alike. */
export interface FundCommitmentSavePayload {
  id?: string
  fund_id?: string
  investor_id?: string
  new_investor?: {
    account_id: string
    name: string
    legal_name?: string | null
    investor_classification_id?: number | null
    sbic_investor_type_id?: number | null
  }
  amount?: number
  status_id?: number
  date_committed?: string | null
  date_closed?: string | null
  management_fee_rate?: number | null
  base_carry_rate?: number | null
  base_hurdle_multiple?: number | null
  stepped_carry_rate?: number | null
  stepped_hurdle_multiple?: number | null
  /** The general partner's own commitment (printed on the ILPA workbook). */
  is_gp_commitment?: boolean
  /** The row the fund's carried interest is allocated and paid to (one per
   *  fund; it may be a commitment of 0). Absent = unchanged. Never sent for a
   *  pocket of a consolidated commitment. */
  is_carry_recipient?: boolean
  /** Where the additional notice documents come from: false = the investor's
   *  (the default; the commitment's own are cleared), true = its own set. */
  notice_supplements_overridden?: boolean
  /** cfg.notice_templates supplements the commitment carries as its OWN set;
   *  its live rows become exactly this set. */
  notice_supplement_template_ids?: number[]
}

export const fetchFundCommitmentEditorOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: FundCommitmentEditorOptions | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.COMMITMENT_OPTIONS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.COMMITMENT_OPTIONS.method, headers })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }
    if (!response.ok) {
      throw new Error(json.error || json.message || `Failed to fetch commitment options: ${response.status}`)
    }
    return { data: validateFundCommitmentEditorOptions(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching commitment options:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Create / edit a standalone commitment. An amount below called-to-date
 * comes back as `belowCalledWarning` (HTTP 409) without writing; re-submit
 * with force=true to save anyway.
 */
export const saveFundCommitment = async (
  payload: FundCommitmentSavePayload,
  force: boolean = false,
  accessToken: string | null = null
): Promise<{
  success: boolean
  /** the saved commitment (a new form navigates to it) */
  fundCommitmentId?: string
  belowCalledWarning?: { newAmount: number; calledToDate: number }
  error?: string
}> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.COMMITMENT_SAVE, {}, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.COMMITMENT_SAVE.method,
      headers,
      body: JSON.stringify({ data: { ...payload, force } })
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }
    if (response.ok || response.status === 409) {
      const result = validateFundCommitmentSaveResult(json.data)
      if (result.status === 'ok') return { success: true, fundCommitmentId: result.fund_commitments__id }
      return {
        success: false,
        belowCalledWarning: { newAmount: result.prog__new_amount, calledToDate: result.prog__called_to_date }
      }
    }
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(json.error || json.message || `Failed to save commitment: ${response.status}`)
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving commitment:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const trashFundCommitment = async (
  fundCommitmentId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.COMMITMENT_TRASH, { id: fundCommitmentId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.COMMITMENT_TRASH.method, headers })
    const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.error || json.message || `Failed to delete commitment: ${response.status}`)
    }
    return { success: true }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error deleting commitment:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface FundTrialBalanceResponse {
  data: FundTrialBalanceRow[]
  finYear: FundLedgerFinYear | null
  totals: FundLedgerTotals | null
  error?: string
}

export const fetchFundTrialBalance = async (
  fundId: string,
  params: { fyId?: string | null; asOf?: string | null; showAllocation?: boolean } = {},
  accessToken: string | null = null
): Promise<FundTrialBalanceResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const query: Record<string, string> = {}
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    // the partner allocation lines of a year close: hidden by default
    if (params.showAllocation) query.show_allocation = 'true'
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.TRIAL_BALANCE, { id: fundId }, query)
    const response = await fetch(url, { method: API_ROUTES.FUNDS.TRIAL_BALANCE.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch trial balance: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      fin_year?: unknown
      totals?: unknown
    }
    return {
      data: validateFundTrialBalanceRows(json.data ?? []),
      finYear: (json.fin_year as any) ?? null,
      totals: (json.totals as any) ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching trial balance:', error)
    return {
      data: [],
      finYear: null,
      totals: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundGlDetail = async (
  fundId: string,
  chartOfAccountsId: string,
  params: { fyId?: string | null; asOf?: string | null; showAllocation?: boolean } = {},
  accessToken: string | null = null
): Promise<{ data: FundGlDetailRow[]; opening?: number | null; openingFmt?: string | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const query: Record<string, string> = { chart_of_accounts_id: chartOfAccountsId }
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    if (params.showAllocation) query.show_allocation = 'true'
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.GL_DETAIL, { id: fundId }, query)
    const response = await fetch(url, { method: API_ROUTES.FUNDS.GL_DETAIL.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch GL detail: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[]; opening?: number; opening_fmt?: string }
    return {
      data: validateFundGlDetailRows(json.data ?? []),
      opening: json.opening ?? null,
      openingFmt: json.opening_fmt ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching GL detail:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundFinYears = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: Array<{ value: string; label: string; closed?: boolean }>; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FIN_YEARS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.FIN_YEARS.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch financial years: ${response.status}`)
    const json = (await response.json()) as { data?: Array<{ value: string; label: string; closed?: boolean }> }
    return { data: json.data ?? [] }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching financial years:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface FundStatementOptionsResponse {
  statementTypes: FundStatementTypeOption[]
  lps: FundStatementLpOption[]
  error?: string
}

export const fetchFundStatementOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundStatementOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.STATEMENT_OPTIONS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.STATEMENT_OPTIONS.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch statement options: ${response.status}`)
    const json = (await response.json()) as { statement_types?: unknown[]; lps?: unknown[] }
    return {
      statementTypes: validateFundStatementTypeOptions(json.statement_types ?? []),
      lps: validateFundStatementLpOptions(json.lps ?? [])
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement options:', error)
    return {
      statementTypes: [],
      lps: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface FundStatementResponse {
  data: FundStatementRow[]
  finYear: FundLedgerFinYear | null
  statement: FundStatementMeta | null
  /** the layout the statement was laid out on */
  layout: FundStatementLayout | null
  /** the statement's columns, in display order; rows carry one amount each */
  columns: FundStatementColumn[]
  totals: Record<string, unknown>
  statusMessage?: string | null
  error?: string
}

export const fetchFundStatement = async (
  fundId: string,
  params: { code: string; fyId?: string | null; asOf?: string | null },
  accessToken: string | null = null
): Promise<FundStatementResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const query: Record<string, string> = { code: params.code }
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.STATEMENT, { id: fundId }, query)
    const response = await fetch(url, { method: API_ROUTES.FUNDS.STATEMENT.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch statement: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      fin_year?: unknown
      statement?: unknown
      layout?: unknown
      columns?: unknown[]
      totals?: Record<string, unknown>
      status_message?: string | null
    }
    return {
      data: validateFundStatementRows(json.data ?? []),
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      statement: (json.statement as FundStatementMeta) ?? null,
      layout: (json.layout as FundStatementLayout) ?? null,
      columns: validateFundStatementColumns(json.columns ?? []),
      totals: json.totals ?? {},
      statusMessage: json.status_message ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement:', error)
    return {
      data: [],
      finYear: null,
      statement: null,
      layout: null,
      columns: [],
      totals: {},
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface FundPartnersCapitalResponse {
  data: FundPartnersCapitalRow[]
  fundTotal: FundPartnersCapitalTotal | null
  finYear: FundLedgerFinYear | null
  allocationNote?: string | null
  error?: string
}

export const fetchFundPartnersCapital = async (
  fundId: string,
  params: { fyId?: string | null; asOf?: string | null } = {},
  accessToken: string | null = null
): Promise<FundPartnersCapitalResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const query: Record<string, string> = {}
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.PARTNERS_CAPITAL, { id: fundId }, query)
    const response = await fetch(url, { method: API_ROUTES.FUNDS.PARTNERS_CAPITAL.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch partners capital: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      fund_total?: unknown
      fin_year?: unknown
      allocation_note?: string | null
    }
    return {
      data: validateFundPartnersCapitalRows(json.data ?? []),
      fundTotal: (json.fund_total as FundPartnersCapitalTotal) ?? null,
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      allocationNote: json.allocation_note ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching partners capital:', error)
    return {
      data: [],
      fundTotal: null,
      finYear: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface FundLpCapitalAccountResponse {
  // statement rows on the fund's layout, one amount per column
  data: FundStatementRow[]
  header: FundLpCapitalAccountHeader | null
  finYear: FundLedgerFinYear | null
  columns: FundStatementColumn[]
  allocationNote?: string | null
  statusMessage?: string | null
  error?: string
}

export const fetchFundLpCapitalAccount = async (
  fundId: string,
  fundCommitmentId: string,
  // fundCommitmentTransferId selects a PRIOR holder's tenure (the transferor
  // of that booked Transfer of Interest); omitted = the current tenure
  params: { fyId?: string | null; asOf?: string | null; fundCommitmentTransferId?: string | null } = {},
  accessToken: string | null = null
): Promise<FundLpCapitalAccountResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const query: Record<string, string> = { fund_commitment_id: fundCommitmentId }
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    if (params.fundCommitmentTransferId) query.fund_commitment_transfer_id = params.fundCommitmentTransferId
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.LP_CAPITAL_ACCOUNT, { id: fundId }, query)
    const response = await fetch(url, { method: API_ROUTES.FUNDS.LP_CAPITAL_ACCOUNT.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch LP capital account: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      header?: unknown
      fin_year?: unknown
      columns?: unknown[]
      allocation_note?: string | null
      status_message?: string | null
    }
    return {
      data: validateFundStatementRows(json.data ?? []),
      header: (json.header as FundLpCapitalAccountHeader) ?? null,
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      columns: validateFundStatementColumns(json.columns ?? []),
      allocationNote: json.allocation_note ?? null,
      statusMessage: json.status_message ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching LP capital account:', error)
    return {
      data: [],
      header: null,
      finYear: null,
      columns: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

/** The Close year dialog's read (fund__fin_year_allocation): the curative
 *  partner allocation of the year per partner and tax category. */
export const fetchFundFinYearAllocation = async (
  fundId: string,
  fyId: string,
  accessToken: string | null = null
): Promise<{ data: FundFinYearAllocation | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FIN_YEAR_ALLOCATION, { id: fundId, fyId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.FIN_YEAR_ALLOCATION.method, headers })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      throw new Error(body.message || body.error || `Failed to fetch the year's allocation: ${response.status}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundFinYearAllocation(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the financial year allocation:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** The Partner allocation report (view__fund_partner_allocation): per
 *  partner and K-1 tax category for a financial year, to `asOf` inside an
 *  open year (null = the year end). */
export const fetchFundPartnerAllocation = async (
  fundId: string,
  fyId: string,
  params: { asOf?: string | null },
  accessToken: string | null = null
): Promise<{ data: FundPartnerAllocation | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUNDS.PARTNER_ALLOCATION,
      { id: fundId, fyId },
      params.asOf ? { as_of: params.asOf } : {}
    )
    const response = await fetch(url, { method: API_ROUTES.FUNDS.PARTNER_ALLOCATION.method, headers })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      throw new Error(body.message || body.error || `Failed to fetch the partner allocation: ${response.status}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundPartnerAllocation(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the partner allocation:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Download the Partner allocation workbook (the report and the audit
 *  detail sheet) and save it under the server's filename. */
export const downloadFundPartnerAllocationWorkbook = async (
  fundId: string,
  fyId: string,
  asOf: string | null,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUNDS.PARTNER_ALLOCATION_WORKBOOK,
      { id: fundId, fyId },
      asOf ? { as_of: asOf } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.PARTNER_ALLOCATION_WORKBOOK.method,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    })
    if (!response.ok) {
      if (response.status === 401) return { success: false, error: 'Authentication failed - please log in again' }
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      return { success: false, error: json.message || json.error || `Failed to download the workbook: ${response.status}` }
    }
    const blob = await response.blob()
    saveBlob(blob, filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'partner-allocation.xlsx'))
    return { success: true }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error downloading the partner allocation workbook:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Download the Review workbook of a fund as of a period end - the
 *  statements, the schedule of investments, the partners' capital, the
 *  trial balance and the journal lines since inception as linked tabs -
 *  and save it under the server's filename. */
export const downloadFundReviewWorkbook = async (
  fundId: string,
  asOf: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.REVIEW_WORKBOOK, { id: fundId }, { as_of: asOf })
    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.REVIEW_WORKBOOK.method,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    })
    if (!response.ok) {
      if (response.status === 401) return { success: false, error: 'Authentication failed - please log in again' }
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      return { success: false, error: json.message || json.error || `Failed to download the workbook: ${response.status}` }
    }
    const blob = await response.blob()
    saveBlob(blob, filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'review-workbook.xlsx'))
    return { success: true }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error downloading the review workbook:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Close a financial year. `overrides` (the GP's equitable adjustments of
 *  the partner allocation, per partner and tax category) travel in the
 *  request envelope; undefined leaves the stored overrides as they are. */
export const closeFundFinYear = async (
  fundId: string,
  fyId: string,
  accessToken: string | null = null,
  overrides?: FinYearAllocationOverride[]
): Promise<{ data: Record<string, unknown> | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FIN_YEAR_CLOSE, { id: fundId, fyId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUNDS.FIN_YEAR_CLOSE.method,
      headers,
      body: JSON.stringify({ data: overrides ? { overrides } : {} })
    })
    const json = (await response.json()) as { data?: Record<string, unknown>; error?: string }
    if (!response.ok) return { data: null, error: json.error || `Failed to close year: ${response.status}` }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error closing financial year:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const reopenFundFinYear = async (
  fundId: string,
  fyId: string,
  accessToken: string | null = null
): Promise<{ data: Record<string, unknown> | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.FIN_YEAR_REOPEN, { id: fundId, fyId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.FIN_YEAR_REOPEN.method, headers })
    const json = (await response.json()) as { data?: Record<string, unknown>; error?: string }
    if (!response.ok) return { data: null, error: json.error || `Failed to reopen year: ${response.status}` }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error reopening financial year:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundTerms = async (fundId: string, accessToken: string | null = null): Promise<FundTermsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.TERMS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.TERMS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund terms: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, unknown> | null }
    if (!json.data) return { data: null }

    return { data: validateFundTermsRecord(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund terms:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ---------------------------------------------------------------------------
// Chart of Accounts tab
// ---------------------------------------------------------------------------
// The write endpoints answer 422 with a raw, user-facing message when a guard
// refuses (deactivating an account that still has journals, reallocating
// across a closed year without a date, a duplicate template name, ...). That
// message is the point - it is surfaced verbatim, not replaced with a generic
// error, so postChartAction reads the body before falling back on the status.

export interface FundChartOfAccountsResponse {
  accounts: FundChartAccountRow[]
  items: FundChartItemRow[]
  hasChart: boolean
  firstOpenFyStart: string | null
  error?: string
}

export const fetchFundChartOfAccounts = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundChartOfAccountsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.CHART_OF_ACCOUNTS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.CHART_OF_ACCOUNTS.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch chart of accounts: ${response.status}`)
    const json = (await response.json()) as {
      data?: { accounts?: unknown[]; items?: unknown[] }
      has_chart?: boolean
      first_open_fy_start?: string | null
    }
    return {
      accounts: validateFundChartAccountRows(json.data?.accounts ?? []),
      items: validateFundChartItemRows(json.data?.items ?? []),
      hasChart: json.has_chart ?? false,
      firstOpenFyStart: json.first_open_fy_start ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching chart of accounts:', error)
    return {
      accounts: [],
      items: [],
      hasChart: false,
      firstOpenFyStart: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface FundChartOptionsResponse {
  glAccounts: ChartGlAccountOption[]
  sourceFunds: ChartSourceFundOption[]
  templates: ChartTemplateOption[]
  error?: string
}

export const fetchFundChartOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<FundChartOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.CHART_OF_ACCOUNTS_OPTIONS, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.CHART_OF_ACCOUNTS_OPTIONS.method, headers })
    if (!response.ok) throw new Error(`Failed to fetch chart options: ${response.status}`)
    const json = (await response.json()) as {
      data?: {
        gl_accounts?: ChartGlAccountOption[]
        source_funds?: ChartSourceFundOption[]
        templates?: ChartTemplateOption[]
      }
    }
    return {
      glAccounts: json.data?.gl_accounts ?? [],
      sourceFunds: json.data?.source_funds ?? [],
      templates: json.data?.templates ?? []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching chart options:', error)
    return {
      glAccounts: [],
      sourceFunds: [],
      templates: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

interface ChartActionResult {
  data: { status_message?: string } | null
  error?: string
}

const postChartAction = async (
  route: { path: string; method: string },
  fundId: string,
  payload: Record<string, unknown>,
  accessToken: string | null,
  fallbackMessage: string
): Promise<ChartActionResult> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, route as any, { id: fundId }, {})
    const response = await fetch(url, {
      method: route.method,
      headers,
      body: JSON.stringify({ data: payload })
    })
    const json = await readJsonBody<{ error?: string; data?: { status_message?: string } }>(response)
    if (!response.ok) {
      // 422 carries the guard's own wording - show that, not the status code
      return { data: null, error: json?.error || `${fallbackMessage}: ${response.status}` }
    }
    return { data: json?.data ?? null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(fallbackMessage, error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const createFundChartFull = (fundId: string, accessToken: string | null = null) =>
  postChartAction(API_ROUTES.FUNDS.CHART_CREATE_FULL, fundId, {}, accessToken, 'Failed to create the chart of accounts')

export const copyFundChartFromFund = (fundId: string, sourceFundId: string, accessToken: string | null = null) =>
  postChartAction(
    API_ROUTES.FUNDS.CHART_COPY_FROM_FUND,
    fundId,
    { source_fund_id: sourceFundId },
    accessToken,
    'Failed to copy the chart of accounts'
  )

export const createFundChartFromTemplate = (fundId: string, templateId: string, accessToken: string | null = null) =>
  postChartAction(
    API_ROUTES.FUNDS.CHART_CREATE_FROM_TEMPLATE,
    fundId,
    { template_id: templateId },
    accessToken,
    'Failed to create the chart from the template'
  )

// ---------------------------------------------------------------------------
// Terms tab - Waterfall section (docs/WATERFALL_CARRY_DESIGN.md, phase 1)
// ---------------------------------------------------------------------------
// The two writes answer 422 with the rule that refused (a tier without a
// type, a fixed split without its figure, a tier group naming a later
// tier, ...): shown verbatim, as the chart-of-accounts writes are.

export interface FundWaterfallResponse {
  data: FundWaterfallRecord | null
  error?: string
}

export const fetchFundWaterfall = async (fundId: string, accessToken: string | null = null): Promise<FundWaterfallResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.WATERFALL, { id: fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUNDS.WATERFALL.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch the fund waterfall: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, unknown> | null }
    if (!json.data) return { data: null }

    return { data: validateFundWaterfallRecord(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the fund waterfall:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface FundWaterfallPositionResponse {
  /** null while the fund has no open waterfall */
  data: FundWaterfallPosition | null
  error?: string
}

/** The Statements tab's Waterfall position panel: the partners' booked
 *  waterfall state and the hypothetical liquidation of their capital accounts
 *  at fair market value as at the date (accrued carried interest). */
export const fetchFundWaterfallPosition = async (
  fundId: string,
  asOf: string,
  accessToken: string | null = null
): Promise<FundWaterfallPositionResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.WATERFALL_POSITION, { id: fundId }, { as_of: asOf })
    const response = await fetch(url, { method: API_ROUTES.FUNDS.WATERFALL_POSITION.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      // the engine says in words why a waterfall cannot run: pass that on
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch the waterfall position: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, unknown> | null }
    if (!json.data) return { data: null }

    return { data: validateFundWaterfallPosition(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the fund waterfall position:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

const postWaterfallAction = async (
  route: { path: string; method: string },
  fundId: string,
  payload: Record<string, unknown>,
  accessToken: string | null,
  fallbackMessage: string
): Promise<FundWaterfallResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, route as any, { id: fundId }, {})
    const response = await fetch(url, {
      method: route.method,
      headers,
      body: JSON.stringify({ data: payload })
    })
    const json = await readJsonBody<{ error?: string; message?: string; data?: Record<string, unknown> }>(response)
    if (!response.ok) {
      // 422 carries the rule's own wording - show that, not the status code
      return { data: null, error: json?.error || json?.message || `${fallbackMessage}: ${response.status}` }
    }
    return { data: json?.data ? validateFundWaterfallRecord(json.data) : null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(fallbackMessage, error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const saveFundWaterfall = (fundId: string, payload: FundWaterfallSavePayload, accessToken: string | null = null) =>
  postWaterfallAction(API_ROUTES.FUNDS.WATERFALL_SAVE, fundId, payload as unknown as Record<string, unknown>, accessToken, 'Failed to save the waterfall')

export const createFundWaterfallFromTemplate = (fundId: string, templateId: string, accessToken: string | null = null) =>
  postWaterfallAction(
    API_ROUTES.FUNDS.WATERFALL_CREATE_FROM_TEMPLATE,
    fundId,
    { template_id: templateId },
    accessToken,
    'Failed to create the waterfall from the template'
  )

/** The year pools of the waterfall: a pool per calendar year with capital
 *  contributed or an investment bought, every investment assigned to the pool
 *  of its purchase year. It only adds, so it is run again when a year begins.
 *  The answer is the fresh waterfall with `year_pools`, what it added. */
export const createFundWaterfallYearPools = (fundId: string, accessToken: string | null = null) =>
  postWaterfallAction(API_ROUTES.FUNDS.WATERFALL_CREATE_YEAR_POOLS, fundId, {}, accessToken, 'Failed to create the year pools')

/** One investment to another pool of the waterfall; refused (with the rule)
 *  once a distribution is booked. */
export const moveFundWaterfallTranche = (
  fundId: string,
  trancheId: string,
  poolId: string,
  accessToken: string | null = null
) =>
  postWaterfallAction(
    API_ROUTES.FUNDS.WATERFALL_MOVE_TRANCHE,
    fundId,
    { tranche_id: trancheId, pool_id: poolId },
    accessToken,
    'Failed to move the investment to the pool'
  )

export const saveFundChartAccounts = (
  fundId: string,
  accounts: Array<{ gl_account_id: string; active: boolean }>,
  accessToken: string | null = null
) =>
  postChartAction(
    API_ROUTES.FUNDS.CHART_ACCOUNTS_SAVE,
    fundId,
    { accounts },
    accessToken,
    'Failed to save the chart accounts'
  )

export const reallocateFundChartItem = (
  fundId: string,
  payload: { item_id: string; new_gl_account_id: string; reclass_date?: string | null; note?: string | null },
  accessToken: string | null = null
) =>
  postChartAction(
    API_ROUTES.FUNDS.CHART_REALLOCATE_ITEM,
    fundId,
    payload,
    accessToken,
    'Failed to reallocate the accounting item'
  )

export const saveFundChartAsTemplate = (fundId: string, name: string, accessToken: string | null = null) =>
  postChartAction(
    API_ROUTES.FUNDS.CHART_SAVE_AS_TEMPLATE,
    fundId,
    { name },
    accessToken,
    'Failed to save the chart as a template'
  )

export interface InvestorStatementChecksResponse {
  data: InvestorStatementChecks | null
  error?: string
}

/** Whether the investor statements of a fund can be made for a period
 *  end (spec A3.14): reconciliation, what blocks, what only warns. */
export const fetchFundStatementChecks = async (
  id: string,
  periodEnd: string,
  accessToken: string | null = null
): Promise<InvestorStatementChecksResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDS.STATEMENT_CHECKS, { id }, { period_end: periodEnd })
    const response = await fetch(url, { method: API_ROUTES.FUNDS.STATEMENT_CHECKS.method, headers })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch statement checks: ${response.status}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateInvestorStatementChecks(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement checks:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
